/**
 * Utilitaires pour le tri et l'affichage des sessions
 * Logique de tri : En cours → Prévu → Terminé → Annulé
 */

/**
 * Récupère l'état d'une session depuis différentes propriétés possibles
 * Utilise uniquement ce que l'API envoie, sans calcul
 */
export function getSessionEtat(session: any): string {
  if (!session) return 'Non défini'
  
  // Chercher l'état dans les propriétés possibles envoyées par l'API
  return session?.etat || 
         session?.etat_libelle || 
         session?.etat_label || 
         session?.etat_session ||
         session?.session?.etat ||
         session?.session?.etat_libelle ||
         session?.session?.etat_label ||
         session?.status ||
         session?.statut ||
         'Non défini'
}

/**
 * Récupère la couleur de l'état d'une session
 */
export function getSessionEtatColor(etat: string): string {
  const etatLower = etat.toLowerCase()
  if (etatLower.includes('en cours') || etatLower === 'en cours') return 'success'
  if (etatLower.includes('prévu') || etatLower === 'prévu' || etatLower === 'prevu') return 'info'
  if (etatLower.includes('terminé') || etatLower === 'terminé' || etatLower === 'termine') return 'primary'
  if (etatLower.includes('annulé') || etatLower === 'annulé' || etatLower === 'annule') return 'error'
  return 'grey'
}

/**
 * Récupère l'ordre de tri d'une session
 * 1 = En cours (priorité la plus haute)
 * 2 = Prévu
 * 3 = Terminé
 * 4 = Annulé
 * 5 = Autres (priorité la plus basse)
 */
export function getSessionSortOrder(session: any): number {
  const etat = getSessionEtat(session).toLowerCase()
  if (etat.includes('en cours') || etat === 'en cours') return 1
  if (etat.includes('prévu') || etat === 'prévu' || etat === 'prevu') return 2
  if (etat.includes('terminé') || etat === 'terminé' || etat === 'termine') return 3
  if (etat.includes('annulé') || etat === 'annulé' || etat === 'annule') return 4
  return 5
}

/**
 * Récupère l'état d'une session depuis une maquette (via session parente)
 */
export function getMaquetteSessionEtat(maquette: any): string {
  if (!maquette) return 'Non défini'
  
  // Essayer directement depuis la maquette
  const directEtat = maquette?.session?.etat || 
                     maquette?.session?.etat_libelle ||
                     maquette?.session?.etat_label ||
                     maquette?.session_etat
  
  if (directEtat) return directEtat
  
  // Essayer depuis session_id si disponible
  if (maquette?.session_id && maquette?.session) {
    return getSessionEtat(maquette.session)
  }
  
  return 'Non défini'
}

/**
 * Récupère l'ordre de tri d'une maquette basé sur l'état de sa session
 */
export function getMaquetteSortOrder(maquette: any): number {
  return getSessionSortOrder({ etat: getMaquetteSessionEtat(maquette) })
}

/**
 * Récupère l'état d'un planning depuis sa maquette (via session parente)
 */

export function getPlanningSessionEtat(planning: any): string {
  if (!planning) return 'Non défini'
  
  // Essayer depuis planning.maquette.session (chemin le plus courant)
  if (planning?.maquette?.session) {
    return getSessionEtat(planning.maquette.session)
  }
  
  // Essayer directement depuis planning.session
  if (planning?.session) {
    return getSessionEtat(planning.session)
  }
  
  // Essayer depuis planning.maquette.session_etat directement
  if (planning?.maquette?.session_etat) {
    return String(planning.maquette.session_etat)
  }
  
  // Essayer depuis planning.session_etat directement
  if (planning?.session_etat) {
    return String(planning.session_etat)
  }
  
  return 'Non défini'
}

/**
 * Récupère l'ordre de tri d'un planning basé sur l'état de la session de sa maquette
 * 1 = En cours (priorité la plus haute)
 * 2 = Prévu
 * 3 = Terminé
 * 4 = Annulé
 * 5 = Autres (priorité la plus basse)
 */
export function getPlanningSortOrder(planning: any): number {
  const etat = getPlanningSessionEtat(planning).toLowerCase()
  // Vérifier directement l'état pour être sûr
  if (etat.includes('en cours') || etat === 'en cours') return 1
  if (etat.includes('prévu') || etat === 'prévu' || etat === 'prevu') return 2
  if (etat.includes('terminé') || etat === 'terminé' || etat === 'termine') return 3
  if (etat.includes('annulé') || etat === 'annulé' || etat === 'annule') return 4
  return 5
}

/**
 * Trie un tableau de sessions selon l'ordre : En cours → Prévu → Terminé → Annulé
 * En cas d'égalité, trie par nom
 */
export function sortSessionsByEtat<T extends any>(sessions: T[]): T[] {
  return [...sessions].sort((a, b) => {
    const orderA = getSessionSortOrder(a)
    const orderB = getSessionSortOrder(b)
    
    if (orderA !== orderB) {
      return orderA - orderB
    }
    
    // Si même ordre, trier par nom
    const nomA = (a as any)?.nom || (a as any)?.session?.nom || ''
    const nomB = (b as any)?.nom || (b as any)?.session?.nom || ''
    return nomA.localeCompare(nomB, 'fr', { sensitivity: 'base', numeric: true })
  })
}

/**
 * Trie un tableau de maquettes selon l'état de leur session
 */
export function sortMaquettesBySessionEtat<T extends any>(maquettes: T[]): T[] {
  return [...maquettes].sort((a, b) => {
    const orderA = getMaquetteSortOrder(a)
    const orderB = getMaquetteSortOrder(b)
    
    if (orderA !== orderB) {
      return orderA - orderB
    }
    
    // Si même ordre, trier par nom
    const nomA = (a as any)?.nom || ''
    const nomB = (b as any)?.nom || ''
    return nomA.localeCompare(nomB, 'fr', { sensitivity: 'base', numeric: true })
  })
}

/**
 * Trie un tableau de plannings selon l'état de la session de leur maquette
 * Ordre : En cours (1) → Prévu (2) → Terminé (3) → Annulé (4) → Autres (5)
 */
export function sortPlanningsBySessionEtat<T extends any>(plannings: T[]): T[] {
  if (!plannings || plannings.length === 0) return []
  
  return [...plannings].sort((a, b) => {
    const orderA = getPlanningSortOrder(a)
    const orderB = getPlanningSortOrder(b)
    
    // Tri principal par ordre d'état (1 = en cours en premier, 3 = terminé après)
    if (orderA !== orderB) {
      return orderA - orderB
    }
    
    // Si même ordre, trier par intitulé alphabétiquement
    const intituleA = (a as any)?.intitule || ''
    const intituleB = (b as any)?.intitule || ''
    return intituleA.localeCompare(intituleB, 'fr', { sensitivity: 'base' })
  })
}

/**
 * Formate le label d'une session pour l'affichage dans un select
 */
export function formatSessionLabel(session: any, includeClasse: boolean = true): string {
  const nom = session?.nom || session?.code || 'Session sans nom'
  if (!includeClasse) return nom
  
  const classeNom = session?.classe?.intitule || 
                    session?.classe?.nom || 
                    session?.classe_session?.intitule ||
                    ''
  
  return classeNom ? `${nom} (${classeNom})` : nom
}

